import { useState } from 'react';
import './ClinicalNotePanel.css';

const NOTE_TYPE_LABELS = {
  clinic:    'Clinic Note',
  procedure: 'Procedure Note',
  nursing:   'Nursing Note',
};

function VitalsGrid({ vitals }) {
  const items = [
    { label: 'BP',     value: vitals.blood_pressure },
    { label: 'HR',     value: vitals.heart_rate ? `${vitals.heart_rate} bpm` : null },
    { label: 'O₂ Sat', value: vitals.o2_sat ? `${vitals.o2_sat}%` : null },
    { label: 'Weight', value: vitals.weight ? `${vitals.weight} lbs` : null },
    { label: 'Height', value: vitals.height ? `${vitals.height} in` : null },
    { label: 'BMI',    value: vitals.bmi },
    { label: 'Temp',   value: vitals.temperature ? `${vitals.temperature}°F` : null },
  ].filter((item) => item.value);

  if (items.length === 0) return null;

  return (
    <div className="cnp-vitals-grid">
      {items.map(({ label, value }) => (
        <div key={label} className="cnp-vital-item">
          <span className="cnp-vital-label">{label}</span>
          <span className="cnp-vital-value">{value}</span>
        </div>
      ))}
    </div>
  );
}

function NoteField({ label, value, highlight }) {
  if (!value) return null;
  return (
    <div className={`cnp-field${highlight ? ' cnp-field-highlight' : ''}`}>
      <span className="cnp-field-label">{label}</span>
      <p className="cnp-field-text">{value}</p>
    </div>
  );
}

// Renders the pipe-delimited diagnosis string as a numbered list.
// Each entry is a visit-specific diagnosis from the provider.
function DiagnosisList({ value }) {
  if (!value) return null;
  const items = value.split('||').map((d) => d.trim()).filter(Boolean);
  if (items.length === 0) return null;

  return (
    <div className="cnp-field cnp-field-diagnosis">
      <span className="cnp-field-label">
        Diagnosis — Visit-Specific
        <span className="cnp-label-tag">Primary ICD-10 Source</span>
      </span>
      <ol className="cnp-diagnosis-list">
        {items.map((item, i) => (
          <li key={i} className="cnp-diagnosis-item">{item}</li>
        ))}
      </ol>
    </div>
  );
}

function ClinicalNotePanel({ note }) {
  const [open, setOpen] = useState(true);

  if (!note) return null;

  const typeLabel = NOTE_TYPE_LABELS[note.type] || 'Clinical Note';

  return (
    <div className="cnp-panel">
      <button
        type="button"
        className="cnp-header"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <div className="cnp-header-left">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
          <span className="cnp-title">Clinical Note</span>
          <span className={`cnp-type-badge cnp-type-${note.type}`}>{typeLabel}</span>
        </div>
        <svg
          className={`cnp-chevron${open ? ' open' : ''}`}
          width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="cnp-body">
          {/* ── Clinic note ───────────────────────────────────── */}
          {note.type === 'clinic' && (
            <>
              <NoteField label="Chief Complaint" value={note.chief_complaint} />
              {/* Visit-specific diagnoses — primary source for ICD-10 code selection */}
              <DiagnosisList value={note.diagnosis} />
              {/* Provider's narrative reasoning — context for complex/multi-problem visits */}
              <NoteField label="Impression" value={note.impression} />
              {/* Assessment = ongoing/chronic conditions; secondary coding context */}
              <NoteField label="Assessment — Ongoing / Chronic Conditions" value={note.assessment} highlight />
              <NoteField label="Plan" value={note.plan} />

              {note.provider_cpt_suggestion && (
                <div className="cnp-cpt-suggestion">
                  <div className="cnp-suggestion-header">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    Provider CPT Suggestion — advisory only, verify before use
                  </div>
                  <span className="cnp-cpt-code">{note.provider_cpt_suggestion}</span>
                </div>
              )}

              {note.vitals && (
                <div className="cnp-section">
                  <span className="cnp-section-label">Vitals</span>
                  <VitalsGrid vitals={note.vitals} />
                </div>
              )}
            </>
          )}

          {/* ── Procedure note ────────────────────────────────── */}
          {note.type === 'procedure' && (
            <>
              {/* Indication → diagnosis code source */}
              <NoteField label="Indication"         value={note.indication} highlight />
              {/* Procedure + location → CPT code source */}
              <NoteField label="Procedure Performed" value={note.procedure} />
              <NoteField label="Site / Location"    value={note.location} />
              <NoteField label="Complications"      value={note.complications} />
              <NoteField label="Disposition"        value={note.disposition} />
            </>
          )}

          {/* ── Nursing note ──────────────────────────────────── */}
          {note.type === 'nursing' && (
            <NoteField label="Nursing Note" value={note.note} />
          )}
        </div>
      )}
    </div>
  );
}

export default ClinicalNotePanel;
